<template>
<ValidationObserver v-slot="{ invalid, handleSubmit, failed }" ref="observer">
  <form @submit.prevent="handleSubmit(saveCampsite)" class="campsite-form" novalidate>
    <div
      class="edit-box box-shadow campsite"
      :class="{
        'is-locked': !isEditMode,
        'is-collapsed': isCollapsed && !isEditMode
      }"
    >
      <div class="columns">
        <div class="column is-4-tablet is-3-desktop" v-if="!isEditMode">
          <div
            class="image-wrapper"
            :class="{ 'is-fallback': !campsiteCopy.image }"
          >
            <div class="image" v-if="campsiteCopy.image">
              <img :src="campsiteCopy.image.variants['4_3_big'].url" alt="" />
            </div>
            <div class="image image--fallback" v-else>
              <i aria-hidden="true" class="fa fa-image fa-3x image-icon"></i>
            </div>
            <router-link
              :to="{
                name: 'campsite-images',
                params: { campsiteId: campsiteCopy.id, lang: lang },
              }"
              class="button is-pulled-left edit-images-button"
            >
              <span class="is-hidden-tablet-only button-label">{{
                $t("campsites.editImages")
              }}</span>
              <i aria-hidden="true" class="fa fa-pencil-square-o fa-lg"></i>
            </router-link>
          </div>
        </div>
        <div
          class="column"
          :class="{ 'is-8-tablet is-9-desktop': !isEditMode }"
        >
          <div class="edit-box__header is-clearfix">
            <div class="edit-box__headline-wrapper is-pulled-left">
              <h3 v-if="!isEditMode" class="edit-box__headline">
                {{ campsiteCopy.name }}
              </h3>
              <h4 v-if="campsiteCopy.code && !isEditMode">
                Code: <strong>{{ campsiteCopy.code }}</strong>
              </h4>
              <ValidationProvider name="campsiteCopy.name" ref="name" v-slot="{ errors }" rules="required" v-if="isEditMode">
                <input
                  type="text"
                  placeholder="Name"
                  class="edit-box--input input input-big"
                  :class="{ 'is-danger': errors[0] }"
                  name="campsiteCopy.name"
                  v-model="campsiteCopy.name"
                  @click="fromFieldClicked('name')"
                />
                <h4 v-if="campsiteCopy.code && isEditMode" class="campsite-code-edit">
                  Code: <strong>{{ campsiteCopy.code }}</strong>
                </h4>
                <span v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <div class="edit-box__toggle-edit is-pulled-right">
              <a
                  class="button"
                  @click="toggleEditMode"
              >
                  <i
                      v-show="isEditMode"
                      class="fa fa-times fa-lg"
                      aria-hidden="true"
                  />
                  <i
                      v-show="!isEditMode"
                      class="fa fa-pencil-square-o fa-lg"
                      aria-hidden="true"
                  />
              </a>
                <Modal
                    :title="$t('campsites.copyTitle')"
                    :submit-button="$t('generic.yes')"
                    :abort-button="$t('generic.no')"
                    :onSubmit="copyCampsite"
                >
                    <p v-html="$t('campsites.copyText', { campsite: campsiteCopy.name })" />
                    <template #activator>
                        <a class="button">
                            <i
                                v-show="!isEditMode"
                                class="fa fa-copy fa-lg"
                                aria-hidden="true"
                            />
                        </a>
                    </template>
                </Modal>
            </div>
          </div>
        </div>
      </div>
      <div class="columns">
        <div class="column is-half">
          <div
            v-if="!isEditMode"
            class="edit-box__section"
            v-html="campsiteCopy.description"
          ></div>
          <div
            v-if="isEditMode"
            class="edit-box__section campsite__season__wysiwyg"
          >
            <wysiwyg
              class="campsite__season__wysiwyg"
              v-model="campsiteCopy.description"
              :options="{ maxChars: 600 }"
              @click.native="fromFieldClicked('description')"
            ></wysiwyg>
          </div>
        </div>
        <div class="column is-half">
            <UseDayPrices :lang="lang" :only-day-prices="onlyDayPrices" :data-copy="campsiteCopy"/>
           <div class="edit-box__section" v-if="!onlyDayPrices || (isEditMode && !campsiteCopy.id)">
            <div
              class="campsite__season"
              v-for="(season, index) in cncSeasons"
              v-if="!season.is_lock"
               :key="'season_'+index"
            >
              <div class="columns is-mobile">
                <div class="column is-half">
                  <label
                    :for="season.name"
                    class="
                      label
                      campsite__season__label
                      edit-box__includes-inline-input
                    "
                  >
                    {{ season.name }}

                    <span class="tooltip-activator d-inline-flex">
                      <i class="fa fa-info icon-border" aria-hidden="true" />
                      <span class="tooltip tooltip--is-large">{{ $t('bookings.newBookingInfo') }}</span>
                    </span>
                  </label>
                </div>
                <div class="column is-half">
                  <span
                    v-if="!isEditMode"
                    class="
                      campsite__season__price
                      edit-box__includes-inline-input
                    "
                    :class="{ 'is-warning': !prices[season.id] }"
                  >
                    <span v-if="prices[season.id] != undefined">
                      {{
                        prices[season.id]
                          | currency($getDefaultCurrency().code, lang)
                      }}
                    </span>
                    <span v-if="prices[season.id] == undefined">
                      <span class="icon is-small"
                        ><i
                          aria-hidden="true"
                          class="fa fa-exclamation-triangle is-invalid"
                        ></i
                      ></span>
                    </span>
                  </span>
                  <ValidationProvider :name="season.name" ref="season" v-slot="{ errors }" rules="required" v-if="isEditMode">
                    <input
                      :id="season.name"
                      class="input edit-box__inline-input is-price"
                      type="number"
                      :class="{
                        'is-danger':
                          errors[0] ||
                          prices[season.id] == undefined,
                      }"
                      :name="season.name"
                      v-model="prices[season.id]"
                      @change="onPriceChanged(season.id, prices[season.id])"
                      min="1"
                      max="999"
                      @click="fromFieldClicked('season_price')"
                    />
                    <span
                      class="edit-box__includes-inline-input"
                      >{{ $getDefaultCurrency().symbol }}</span
                    >
                    <span
                      v-show="errors[0]"
                      class="help is-danger"
                      >{{ errors[0] }}</span
                    >
                  </ValidationProvider>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <hr class="edit-box__seperator" />

      <div class="columns">
        <div class="column is-half">
          <div class="edit-box__section">
            <label for="campsite_type" class="label">
              {{ $t("campsites.typeCampsite") }}
            </label>
            <span v-if="!isEditMode" class="edit-box__includes-inline-input">{{
              $t(
                "generic.campsite_types." + campsiteCopy.campsite_type + ".name"
              )
            }}</span>
            <ValidationProvider name="campsite_type" ref="campsite_type" v-slot="{ errors }" rules="required" v-if="isEditMode">
              <span class="select">
                <select
                  id="campsite_type"
                  v-model="campsiteCopy.campsite_type"
                  :class="{ 'is-danger': errors[0] }"
                  name="campsite_type"
                  @click="fromFieldClicked('campsite_type')"
                >
                  <option v-for="(value, key) in campsiteTypes" :value="key">
                    {{ value }}
                  </option>
                </select>
              </span>
              <span v-show="errors[0]" class="help is-danger">{{
                errors[0]
              }}</span>
            </ValidationProvider>
          </div>
          <div class="edit-box__section">
            <label for="campsiteCopy.quantity" class="label">{{
              $t("campsites.quantity")
            }}</label>
            <span v-if="!isEditMode" class="edit-box__includes-inline-input">{{
              campsiteCopy.quantity
            }}</span>
            <ValidationProvider name="campsiteCopy.quantity" ref="quantity" v-slot="{ errors }" rules="required" v-if="isEditMode">
              <input
                id="campsiteCopy.quantity"
                v-model="campsiteCopy.quantity"
                name="campsiteCopy.quantity"
                class="input edit-box__inline-input is-countbig"
                type="number"
                :class="{ 'is-danger': errors[0] }"
                min="0"
                max="999"
                @click="fromFieldClicked('quantity')"
              />
              <span
                v-show="errors[0]"
                class="help is-danger"
                >{{ errors[0] }}</span
              >
            </ValidationProvider>

          </div>
          <div class="edit-box__section">
            <label for="campsiteCopy.reduce_quantity" class="label">{{
              $t("campsites.reduce_quantity")
            }}</label>
            <span v-if="!isEditMode" class="edit-box__includes-inline-input">{{
              campsiteCopy.reduce_quantity || 0
            }}</span>
            <ValidationProvider name="campsiteCopy.reduce_quantity" ref="reduce_quantity" v-slot="{ errors }" rules="required" v-if="isEditMode">
              <input
                id="campsiteCopy.reduce_quantity"
                v-model="campsiteCopy.reduce_quantity"
                name="campsiteCopy.reduce_quantity"
                class="input edit-box__inline-input is-countbig"
                type="number"
                :class="{ 'is-danger': errors[0] }"
                min="0"
                max="999"
                @click="fromFieldClicked('reduce_quantity')"
              />
              <p class="is-info">{{ $t("campsites.reduce_quantity_help")}}</p>
              <span
                v-show="errors[0]"
                class="help is-danger"
                >{{ errors[0] }}</span
              >
            </ValidationProvider>
          </div>



        <div class="columns">
            <div class="column is-12">
                <div class="edit-box__section">
                    <label for="transit_item" class="checkbox label">
                        {{ $t('campsites.active') }}:
                        <span class="icon is-small" v-if="!isEditMode">
                            <i aria-hidden="true" class="fa fa-check-circle is-valid" v-if="campsiteCopy.active"></i>
                            <i aria-hidden="true" class="fa fa-minus-circle is-invalid" v-else></i>
                        </span>
                        <input v-if="isEditMode"
                            class="edit-box__inline-input"
                            type="checkbox"
                            id="active"
                            name="active"
                            v-model="campsiteCopy.active"
                            @click="fromFieldClicked('active')"
                            >
                    </label>
                    <div class="">{{ $t('campsites.activeHelp') }}</div>
                </div>
            </div>
        </div>

        <div class="columns">
            <div class="column is-12">
                <div class="edit-box__section">
                    <br>
                    <label for="can_be_free" class="checkbox label">
                        {{ $t('persons.can_be_free') }}:
                        <span class="icon is-small" v-if="!isEditMode">
                            <i aria-hidden="true" class="fa fa-check-circle is-valid" v-if="campsiteCopy.can_be_free"></i>
                            <i aria-hidden="true" class="fa fa-minus-circle is-invalid" v-else></i>
                        </span>

                        <input v-if="isEditMode"
                            class="edit-box__inline-input"
                            type="checkbox"
                            id="can_be_free"
                            name="can_be_free"
                            v-model="campsiteCopy.can_be_free"
                            @click="fromFieldClicked('can_be_free')"
                            >
                    </label>
                    <span>{{ $t('persons.can_be_free_info') }}</span>
                </div>
            </div>
        </div>


        <div class="columns">
            <div class="column is-12">
                <div class="edit-box__section" data-gksw="WIP">
                    <label for="campsite_cancellationTerm" class="label">
                      {{ $t("campsites.cancellationHeader") }}
                    </label>
                    <span v-if="!isEditMode">
                        {{ getCancellationTermName(campsiteCopy.cancellation_term_id) }}
                    </span>

                  <ValidationProvider name="cancellation_term_id" ref="campsite_cancellation_term" v-slot="{ errors }" v-if="isEditMode">
                      <span class="select">
                        <select
                          id="campsite_type"
                          v-model="campsiteCopy.cancellation_term_id"
                          :class="{ 'is-danger': errors[0] } "
                          name="campsite_type"
                        >
                          <option :value=null>-</option>
                          <option v-for="(value, key) in getCancellationTerms()" :value="value.id">
                            {{value.pms_key}} : {{ value.name }}
                          </option>
                        </select>
                      </span>
                    </ValidationProvider>
                </div>
            </div>
        </div>

        <div class="columns">
            <div class="column is-12">
                <div class="edit-box__section" data-gksw="WIP">
                    <label for="campsite_cancellationTerm" class="label">
                      {{ $t("extras.external_id") }}
                    </label>
                    <span v-if="!isEditMode">
                        {{ campsiteCopy.external_id }}
                    </span>
                    <input v-if="isEditMode"
                           class="edit-box--input input is-inline-block"
                           style="width: 50%"
                           type="text"
                           id="external_id"
                           name="extraCopy.external_id"
                           v-model="campsiteCopy.external_id"
                    >
                    <div v-if="isEditMode" style="margin-top: 10px">
                        {{ $t('extras.external_id_info') }}
                    </div>

                </div>
            </div>
        </div>

        <div class="columns">
            <div class="column is-12" v-if="value.id">
                <label class="label">
                    {{ $t("campsites.suitableFor") }}

                    <a
                        v-if="isEditMode"
                        class="edit-button"
                        @click="toggleSuitableModal"
                    >
                        <i
                            aria-hidden="true"
                            class="fa fa-pencil-square-o fa-lg"
                        />
                    </a>
                </label>

                <Suitables
                    v-if="showSuitableModal"
                    :all-suitables="allSuitables"
                    :active-suitables="activeSuitables"
                    @toggle-modal="toggleSuitableModal"
                    @save="saveSuitables"
                />

                <ul
                    v-if="activeSuitables.length > 0"
                    class="attribute-list"
                >
                    <li
                        :key="`${suitable.name + index}`"
                        v-for="(suitable, index) in activeSuitables"
                        class="attribute-item"
                    >
                        <i
                            class="fa fa-check-circle is-valid"
                            aria-hidden="true"
                        />
                        {{ suitable.name }}
                    </li>
                </ul>
                <p v-else>{{ $t('suitables.noEntries') }}</p>
            </div>
        </div>

        <div class="columns">
            <div class="column is-12">
                <label class="label">
                    {{ $t('targetTypes.title') }}

                    <a
                        v-if="isEditMode"
                        class="edit-button"
                        @click="toggleTargetTypesModal"
                    >
                        <i
                            aria-hidden="true"
                            class="fa fa-pencil-square-o fa-lg"
                        />
                    </a>
                </label>

                <TargetTypes
                    v-if="showTargetTypesModal"
                    :all-target-types="getTargetTypes()"
                    :existing="campsiteCopy.target_types || []"
                    @toggle-modal="toggleTargetTypesModal"
                    @save="saveTargetTypes"
                />
                <ul v-if="(campsiteCopy.target_types || []).length > 0" class="attribute-list two-column">
                    <li
                        v-for="(entry, index) in campsiteCopy.target_types"
                        :key="'tt_'+targetTypeIdOf(entry, index)"
                        class="attribute-item"
                    >
                        <i class="fa fa-check-circle is-valid" aria-hidden="true" />
                        {{ targetTypeLabel(entry) }}:
                        <span v-if="entry.distance_min || entry.distance_max">
                            {{ $t('targetTypes.distanceShort', { min: entry.distance_min, max: entry.distance_max }) }}
                        </span>
                        <span v-if="entry.walk_time_min || entry.walk_time_max">
                             {{ $t('targetTypes.walkTimeShort', { min: entry.walk_time_min || '–', max: entry.walk_time_max || '–' }) }}
                        </span>
                        <span v-if="entry.accessible"> · {{ $t('targetTypes.accessible') }}</span>
                    </li>
                </ul>
                <p v-else>{{ $t('targetTypes.noEntries') }}</p>
            </div>
        </div>

          <div class="edit-box__section" v-if="!onlyDayPrices || !campsiteCopy.id">
            <label class="label">{{
              $t("campsites.arrivalWeekdaysHeadline")
            }}</label>

            <label
              class="checkbox"
              v-for="(weekday, idx) in weekdays"
              :key="idx"
              v-if="Number(idx) !== 0"
            >
              <span class="icon is-small" v-if="!isEditMode">
                <i
                  aria-hidden="true"
                  class="fa fa-check-circle is-valid"
                  v-if="campsiteCopy.arrival_weekdays[idx]"
                ></i>
                <i
                  aria-hidden="true"
                  class="fa fa-minus-circle is-invalid"
                  v-else
                ></i>
              </span>
              <input
                v-if="isEditMode"
                class="edit-box__inline-input"
                type="checkbox"
                :id="`arrival_weekdays_${idx}`"
                :name="`arrival_weekdays_${idx}`"
                v-model="campsiteCopy.arrival_weekdays[idx]"
                @click="fromFieldClicked(`arrival_weekdays_${idx}`)"
              />
              {{ weekday }}
            </label>

            <label
              class="checkbox"
              v-for="(weekday, idx) in weekdays"
              :key="idx"
              v-if="Number(idx) === 0"
            >
              <span class="icon is-small" v-if="!isEditMode">
                <i
                  aria-hidden="true"
                  class="fa fa-check-circle is-valid"
                  v-if="campsiteCopy.arrival_weekdays[idx]"
                ></i>
                <i
                  aria-hidden="true"
                  class="fa fa-minus-circle is-invalid"
                  v-else
                ></i>
              </span>
              <input
                v-if="isEditMode"
                class="edit-box__inline-input"
                type="checkbox"
                :id="`arrival_weekdays_${idx}`"
                :name="`arrival_weekdays_${idx}`"
                v-model="campsiteCopy.arrival_weekdays[idx]"
                @click="fromFieldClicked(`arrival_weekdays_${idx}`)"
              />
              {{ weekday }}
            </label>
          </div>

          <div class="edit-box__section" v-if="!onlyDayPrices || !campsiteCopy.id">
            <label class="label">{{
              $t("campsites.departureWeekdaysHeadline")
            }}</label>

            <label
              class="checkbox"
              v-for="(weekday, idx) in weekdays"
              :key="idx"
              v-if="Number(idx) !== 0"
            >
              <span class="icon is-small" v-if="!isEditMode">
                <i
                  aria-hidden="true"
                  class="fa fa-check-circle is-valid"
                  v-if="campsiteCopy.departure_weekdays[idx]"
                ></i>
                <i
                  aria-hidden="true"
                  class="fa fa-minus-circle is-invalid"
                  v-else
                ></i>
              </span>
              <input
                v-if="isEditMode"
                class="edit-box__inline-input"
                type="checkbox"
                :id="`departure_weekdays_${idx}`"
                :name="`departure_weekdays_${idx}`"
                v-model="campsiteCopy.departure_weekdays[idx]"
                @click="fromFieldClicked(`departure_weekdays_${idx}`)"
              />
              {{ weekday }}
            </label>

            <label
              class="checkbox"
              v-for="(weekday, idx) in weekdays"
              :key="idx"
              v-if="Number(idx) === 0"
            >
              <span class="icon is-small" v-if="!isEditMode">
                <i
                  aria-hidden="true"
                  class="fa fa-check-circle is-valid"
                  v-if="campsiteCopy.departure_weekdays[idx]"
                ></i>
                <i
                  aria-hidden="true"
                  class="fa fa-minus-circle is-invalid"
                  v-else
                ></i>
              </span>
              <input
                v-if="isEditMode"
                class="edit-box__inline-input"
                type="checkbox"
                :id="`departure_weekdays_${idx}`"
                :name="`departure_weekdays_${idx}`"
                v-model="campsiteCopy.departure_weekdays[idx]"
                @click="fromFieldClicked(`departure_weekdays_${idx}`)"
              />
              {{ weekday }}
            </label>
          </div>
        </div>
        <div class="column is-half">
          <div class="edit-box__section">
            <label for="campsiteCopy.included_adults" class="label">{{
              $t("campsites.included")
            }}</label>
            <span class="edit-box__includes-inline-input">
              <span v-if="!isEditMode">{{ campsiteCopy.included_adults }}</span>
              <ValidationProvider name="campsiteCopy.included_adults" ref="included_adults" v-slot="{ errors }" rules="required">
                <input
                  v-if="isEditMode"
                  id="campsiteCopy.included_adults"
                  v-model="campsiteCopy.included_adults"
                  name="campsiteCopy.included_adults"
                  class="input edit-box__inline-input is-count"
                  type="number"
                  :class="{
                    'is-danger': errors[0],
                  }"
                  min="0"
                  max="999"
                  @click="fromFieldClicked('included_adults')"
                />
                {{ $t("generic.adults") }}
                <span v-if="isEditMode" v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
              </ValidationProvider>

              <span v-if="!isEditMode">{{ campsiteCopy.included_children }}</span>
              <ValidationProvider name="campsiteCopy.included_children" ref="included_children" v-slot="{ errors }" rules="required">
                <input
                  v-if="isEditMode"
                  id="campsiteCopy.included_children"
                  v-model="campsiteCopy.included_children"
                  name="campsiteCopy.included_children"
                  class="input edit-box__inline-input is-count"
                  type="number"
                  :class="{
                    'is-danger': errors[0],
                  }"
                  min="0"
                  max="999"
                  @click="fromFieldClicked('included_children')"
                />
                {{ $t("generic.children") }}
                <span v-if="isEditMode" v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
              </ValidationProvider>

              <span v-if="!isEditMode">{{ campsiteCopy.included_pets }}</span>
              <ValidationProvider name="campsiteCopy.included_pets" ref="included_pets" v-slot="{ errors }" rules="required">
                <input
                  v-if="isEditMode"
                  id="campsiteCopy.included_pets"
                  v-model="campsiteCopy.included_pets"
                  name="campsiteCopy.included_pets"
                  class="input edit-box__inline-input is-count"
                  type="number"
                  :class="{
                    'is-danger': errors[0],
                  }"
                  min="0"
                  max="999"
                  @click="fromFieldClicked('included_pets')"
                />
                {{ $t("generic.pets") }}
                <span v-if="isEditMode" v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </span>
          </div>

          <div class="edit-box__section">
            <label for="campsiteCopy.min_persons" class="label">{{
              $t("campsites.minOccupancy")
            }}</label>
            <span class="edit-box__includes-inline-input">
              <span v-if="!isEditMode">{{ campsiteCopy.min_persons }}</span>
              <ValidationProvider name="campsiteCopy.min_persons" v-slot="{ errors }" rules="required">
                <input
                  v-if="isEditMode"
                  id="campsiteCopy.min_persons"
                  v-model="campsiteCopy.min_persons"
                  name="campsiteCopy.min_persons"
                  class="input edit-box__inline-input is-count"
                  type="number"
                  :class="{ 'is-danger': errors[0] }"
                  min="0"
                  max="999"
                  @click="fromFieldClicked('min_persons')"
                />
                {{ $t("generic.menu.persons") }}
                <span v-if="isEditMode" v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </span>
          </div>


          <div class="edit-box__section">
            <label for="campsiteCopy.max_persons" class="label">{{
              $t("campsites.maxOccupancy")
            }}</label>
            <span class="edit-box__includes-inline-input">
              <span v-if="!isEditMode">{{ campsiteCopy.max_persons }}</span>
              <ValidationProvider name="campsiteCopy.max_persons" ref="max_persons" v-slot="{ errors }" rules="required">
                <input
                  v-if="isEditMode"
                  id="campsiteCopy.max_persons"
                  v-model="campsiteCopy.max_persons"
                  name="campsiteCopy.max_persons"
                  class="input edit-box__inline-input is-count"
                  type="number"
                  :class="{ 'is-danger': errors[0] }"
                  min="1"
                  max="999"
                  @click="fromFieldClicked('max_persons')"
                />
                {{ $t("generic.menu.persons") }}
                <span v-if="isEditMode" v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
              </ValidationProvider>

              <span v-if="!isEditMode">{{ campsiteCopy.max_pets }}</span>
              <ValidationProvider name="campsiteCopy.max_pets" ref="max_pets" v-slot="{ errors }" rules="required">
                <input
                  v-if="isEditMode"
                  id="campsiteCopy.max_pets"
                  v-model="campsiteCopy.max_pets"
                  name="campsiteCopy.max_pets"
                  class="input edit-box__inline-input is-count"
                  type="number"
                  :class="{ 'is-danger': errors[0] }"
                  min="0"
                  max="999"
                  @click="fromFieldClicked('max_pets')"
                />
                {{ $t("generic.pets") }}
                <span v-if="isEditMode" v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </span>
          </div>
          <!-- size of the accommodation -->
          <div class="edit-box__section">
            <label for="campsiteCopy.included_adults" class="label">{{$t("campsites.size_info_min")}}</label>
            <span class="edit-box__includes-inline-input">
              <span v-if="!isEditMode">{{ campsiteCopy.min_length }}</span>
              <ValidationProvider name="campsiteCopy.min_length" ref="min_length" v-slot="{ errors }">
                <input
                  v-if="isEditMode"
                  id="campsiteCopy.min_length"
                  v-model="campsiteCopy.min_length"
                  name="campsiteCopy.min_length"
                  class="input edit-box__inline-input is-countbig"
                  type="number"
                  :class="{
                    'is-danger': errors[0],
                  }"
                  min="0"
                  max="999"
                  @click="fromFieldClicked('min_length')"
                />
                {{ $t("campsites.size_length") }},
                <span v-if="isEditMode" v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
              </ValidationProvider>

              <span v-if="!isEditMode">{{ campsiteCopy.min_width }}</span>
              <ValidationProvider name="campsiteCopy.min_width" ref="min_width" v-slot="{ errors }">
                <input
                  v-if="isEditMode"
                  id="campsiteCopy.min_width"
                  v-model="campsiteCopy.min_width"
                  name="campsiteCopy.min_width"
                  class="input edit-box__inline-input is-countbig"
                  type="number"
                  :class="{
                    'is-danger': errors[0],
                  }"
                  min="0"
                  max="999"
                  @click="fromFieldClicked('min_width')"
                />
                {{ $t("campsites.size_width") }},
                <span v-if="isEditMode" v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
              </ValidationProvider>

              <span v-if="!isEditMode">{{ campsiteCopy.min_area }}</span>
              <ValidationProvider name="campsiteCopy.area" ref="min_area" v-slot="{ errors }">
                <input
                  v-if="isEditMode"
                  id="campsiteCopy.min_area"
                  v-model="campsiteCopy.min_area"
                  name="campsiteCopy.min_area"
                  class="input edit-box__inline-input is-countbig"
                  type="number"
                  :class="{
                    'is-danger': errors[0],
                  }"
                  min="0"
                  max="999"
                  @click="fromFieldClicked('min_area')"
                />
                {{ $t("campsites.size_area") }}
                <span v-if="isEditMode" v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </span>
          </div>
          <!-- end of size -->
          <!-- size of the accommodation -->
          <div class="edit-box__section">
            <label for="campsiteCopy.included_adults" class="label">{{$t("campsites.size_info_max")}}</label>
            <span class="edit-box__includes-inline-input">
              <span v-if="!isEditMode">{{ campsiteCopy.max_length }}</span>
              <ValidationProvider name="campsiteCopy.length" ref="max_length" v-slot="{ errors }">
                <input
                  v-if="isEditMode"
                  id="campsiteCopy.max_length"
                  v-model="campsiteCopy.max_length"
                  name="campsiteCopy.max_length"
                  class="input edit-box__inline-input is-countbig"
                  type="number"
                  :class="{
                    'is-danger': errors[0],
                  }"
                  min="0"
                  max="999"
                  @click="fromFieldClicked('max_length')"
                />
                {{ $t("campsites.size_length") }},
                <span v-if="isEditMode" v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
              </ValidationProvider>

              <span v-if="!isEditMode">{{ campsiteCopy.max_width }}</span>
              <ValidationProvider name="campsiteCopy.max_width" ref="width" v-slot="{ errors }">
                <input
                  v-if="isEditMode"
                  id="campsiteCopy.max_width"
                  v-model="campsiteCopy.max_width"
                  name="campsiteCopy.max_width"
                  class="input edit-box__inline-input is-countbig"
                  type="number"
                  :class="{
                    'is-danger': errors[0],
                  }"
                  min="0"
                  max="999"
                  @click="fromFieldClicked('max_width')"
                />
                {{ $t("campsites.size_width") }},
                <span v-if="isEditMode" v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
              </ValidationProvider>

              <span v-if="!isEditMode">{{ campsiteCopy.max_area }}</span>
              <ValidationProvider name="campsiteCopy.area" ref="max_area" v-slot="{ errors }">
                <input
                  v-if="isEditMode"
                  id="campsiteCopy.max_area"
                  v-model="campsiteCopy.max_area"
                  name="campsiteCopy.max_area"
                  class="input edit-box__inline-input is-countbig"
                  type="number"
                  :class="{
                    'is-danger': errors[0],
                  }"
                  min="0"
                  max="999"
                  @click="fromFieldClicked('max_area')"
                />
                {{ $t("campsites.size_area") }}
                <span v-if="isEditMode" v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </span>
          </div>
          <div class="edit-box__section">
            <label for="surface" class="label">
              {{ $t("campsites.surfaceType") }}
            </label>
            <span v-if="!isEditMode" class="edit-box__includes-inline-input">
              {{ getSurfaceName(campsiteCopy.surface)}}
            </span>
            <ValidationProvider name="surface" ref="surface" v-slot="{ errors }" v-if="isEditMode">
              <span class="select">
                <select
                  id="campsite_type"
                  v-model="campsiteCopy.surface"
                  :class="{ 'is-danger': errors[0] }"
                  name="surface"
                  @click="fromFieldClicked('campsite_type')"
                >
                    <option :value="null">-</option>
                  <option v-for="(item, id) in getSurfaces()" :value="item.id">
                    {{ item.name }}                   </option>
                </select>
              </span>
              <span v-show="errors[0]" class="help is-danger">{{
                errors[0]
              }}</span>
            </ValidationProvider>
          </div>
          <div v-if="value.id">
                <label class="label">
                    {{ $t("campsites.attributes") }}

                    <a
                        v-if="isEditMode"
                        class="edit-button"
                        @click="toggleAttributesModal"
                    >
                        <i
                            aria-hidden="true"
                            class="fa fa-pencil-square-o fa-lg"
                        />
                    </a>
                </label>

                <Attributes
                    v-if="showAttributesModal"
                    :all-attributes="allAttributes"
                    :active-attributes="activeAttributes"
                    :accommodation-type="value.campsite_type"
                    @toggle-modal="toggleAttributesModal"
                    @save="saveAttributes"
                />

                <ul
                    v-if="activeAttributes.length > 0"
                    class="attribute-list"
                >
                    <li
                        :key="`${attribute.name + index}`"
                        v-for="(attribute, index) in activeAttributes"
                        class="attribute-item"
                    >
                        <i
                            class="fa"
                            :class="{
                                'fa-check-circle is-valid': attribute.positive_flag,
                                'fa-minus-circle is-invalid': !attribute.positive_flag
                            }"
                            aria-hidden="true"
                        />
                        {{ attribute.name }}
                    </li>
                </ul>
                <p v-else>{{ $t('attributes.noEntries') }}</p>
            </div>

        </div>
      </div>

      <!--- -->
      <h3 style="margin-top: .5rem;">{{ $t('campsites.top3Attributes') }}</h3>
      <div class="columns is-4">
        <div
          class="column is-4"
          v-for="(field, idx) in ['top1_attribute', 'top2_attribute', 'top3_attribute']"
          :key="field"
        >
          <div class="edit-box__section">
            <label :for="field" class="label">
              Top {{ idx + 1 }}
            </label>
            <span v-if="!isEditMode" class="edit-box__includes-inline-input">
              <span
                v-if="topXById(campsiteCopy[field])"
                style="display:inline-flex; align-items:center; gap:6px;"
              >
                <span
                  v-if="topXById(campsiteCopy[field]).icon_svg"
                  v-html="topXById(campsiteCopy[field]).icon_svg"
                />
                <span v-if="topXById(campsiteCopy[field]).name">{{ topXById(campsiteCopy[field]).name }}</span>
              </span>
            </span>
            <ValidationProvider :name="field" :ref="field" v-slot="{ errors }" v-if="isEditMode">
              <span class="select">
                <select
                  :id="field"
                  v-model="campsiteCopy[field]"
                  :class="{ 'is-danger': errors[0] }"
                  :name="field"
                >
                  <option :value="null">-</option>
                  <option v-for="(item, id) in getTopXAttributes()" :key="item.id" :value="item.id">
                    {{ item.name }}
                  </option>
                </select>
              </span>
              <span v-show="errors[0]" class="help is-danger">{{ errors[0] }}</span>
            </ValidationProvider>
          </div>
        </div>
      </div>
        <!-- -->


      <!-- backend errors -->
      <div class="error-messages" v-if="isEditMode">
        <span class="help is-danger" v-for="(value, key) in apiErrors">
          <span v-for="msg in value" v-html="msg"></span>
        </span>
      </div>

      <div class="edit-box__footer is-clearfix" v-if="isEditMode">
        <a
          class="button is-link is-pulled-left"
          v-if="campsiteCopy.id"
          @click="deleteCampsite()"
        >
          <span class="icon">
            <i class="fa fa-trash" aria-hidden="true"></i>
          </span>
          <span>{{ $t("generic.delete") }}</span>
        </a>
        <button
          class="button is-primary is-pulled-right"
          :class="{ 'is-loading': isLoading, 'is-disabled': failed }"
        >
          {{ $t("generic.save") }}
        </button>
        <a class="button is-link is-pulled-right" @click="toggleEditMode()">{{
          $t("generic.cancel")
        }}</a>
      </div>
      <div class="edit-box__dragger" v-if="!isEditMode">
        <div class="sort-toolbar">
          <div class="icon fa fa-angle-double-up" @click="setSort('top')"></div>
          <div class="icon fa fa-angle-up" @click="setSort('up')"></div>
          <div class="icon fa fa-angle-down" @click="setSort('down')"></div>
          <div
            class="icon fa fa-angle-double-down"
            @click="setSort('bottom')"
          ></div>
        </div>
        <div class="collapse-handle" @click="collapseBox">
            {{ campsiteCopy.code }}
            |
            {{ campsiteCopy.name }}
            |
            {{$t("generic.campsite_types." + campsiteCopy.campsite_type + ".name") }}
            |
            {{ campsiteCopy.quantity }} {{ $t("generic.units")}}
            <i  v-if="!campsiteCopy.active" class="fa fa-lock fa-lg" style="color:red"></i>
        </div>
        <div class="handle">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>

    <div
      class="modal quantity-too-low is-success"
      :class="{ 'is-active': showQuantityTooLowWarning }"
    >
      <div class="modal-background"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ $t("campsites.warnings.quantityTooLow.title") }}
          </p>
        </header>
        <section class="modal-card-body">
          <div class="left-right-wrapper">
            <div class="left">
              <i class="icon yellow fa fa-exclamation-triangle"></i>
            </div>
            <div class="right">
              <div
                v-html="
                  $t('campsites.warnings.quantityTooLow.body', {
                    quantity: campsiteCopy.quantity,
                    minQuantity: minQuantity,
                  })
                "
              ></div>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <div class="block">
            <button
              class="button is-link"
              @click.prevent="hideQuantityTooLowWarning(false)"
              v-html="$t('campsites.warnings.quantityTooLow.cancel')"
            ></button>
            <button
              class="button is-primary"
              @click.prevent="hideQuantityTooLowWarning(true)"
            >
              {{ $t("campsites.warnings.quantityTooLow.okay") }}
            </button>
          </div>
        </footer>
      </div>
    </div>
  </form>
  </ValidationObserver>
</template>

<script>
    import {cloneDeep, find} from 'lodash';
    import filters from '../../../filters';
    import { Api } from '../../../api';
    import { AppModel } from '../../../models';
    import { EventBus, Tracking } from '../../../utils';
    import { Wysiwyg } from '../../components';
    import Attributes from '../attributes';
    import Suitables from '../suitables';
    import TargetTypes from '../target-types';
    import UseDayPrices from '../tools/UseDayPrices.vue';
    import cancellationTerms from '../../pages/cancellation-terms/cancellationTerms.vue';
    import Modal from '../modal/modal.vue';

    export default {
        name: 'campsite',
        props: {
            value: {
                type: Object,
                'default': () => ([]),
            },

            seasons: {
                type: Array,
                'default': () => ([]),
            },

            attributeCategories: {
                type: Object,
                default: () => {},
            },

            allAttributes: {
                type: Array,
                default: () => [],
            },

            allSuitables: {
                type: Array,
                default: () => [],
            },

            suitables: {
                type: Array,
                default: () => [],
            },

            campsiteCount: {
                type: Number,
                default: 0,
            }
        },

        filters: filters,

        components: {
            UseDayPrices,
            Attributes,
            Suitables,
            TargetTypes,
            Wysiwyg,
            Modal,
        },

        data() {
            return {
                prices: {},
                campsiteCopy: {},
                isEditMode: !this.value.id, // if seasonDataCopyCopyCopy has no id, display it in edit mode
                isLoading: false,
                showQuantityTooLowWarning: false,
                oldQuantity: 0,
                minQuantity: 0,
                apiErrors: {},
                weekdays: {},
                cancellation_terms: [],
                showAttributesModal: false,
                showSuitableModal: false,
                showTargetTypesModal: false,
                isCollapsed: this.campsiteCount > 2,
                onlyDayPrices: AppModel.onlyDayPrices,
                cncSeasons: this.getSeasons(),
            };
        },

        created() {
            this.$t('generic.calendar.weeknames.long').forEach((day, idx) => {
                this.weekdays[idx] = day;
            });
            // copy original data to object
            this.campsiteCopy = Object.assign({}, cloneDeep(this.value));
            this.campsiteTypes = AppModel.campsiteTypes;
            this.oldQuantity = this.value.quantity;
            if(!this.campsiteCopy.target_types) {
                this.$set(this.campsiteCopy, 'target_types', []);
            }
            this.buildModel();
        },

        computed: {
            cancellationTerms() {
                return cancellationTerms;
            },
            lang() {
                return AppModel.lang;
            },
            activeAttributes() {
                if(this.attributeCategories && this.attributeCategories.attributes) {
                    const attributes = this.attributeCategories.attributes.map((categories) => categories.attributes).flat();

                    return attributes.filter((attr) => {
                        if(this.value.campsite_type === 'accomodation') {
                            return attr.active && attr.valid_for_rentals;
                        }

                        return attr.active && attr.valid_for_pitches;
                    });
                }

                return [];
            },
            activeSuitables() {
                const suits = [];

                this.suitables.forEach((suitable) => {
                    if(suitable.id === this.campsiteCopy.id) {
                        suits.push(...suitable.suitables);
                    }
                });

                return suits;
            },
        },

        validator: null,

        watch: {
            seasons() {
                this.buildModel();
            },

            isEditMode(newValue, oldValue) {
                if(newValue === false) {
                    this.apiErrors = {};
                }
            },
        },

        methods: {
            getCancellationTerms() {
                return AppModel.activeCampground.cancellation_terms;
            },
            getSurfaces() {
                return AppModel.surfaces;
            },
            getSurfaceName(surfaceId) {
                if(!surfaceId) return '';
                const surface = AppModel.surfaces.find(s => s.id === surfaceId);
                return surface ? surface.name : '';
            },
            getTopXAttributes() {
                return AppModel.topXAttributes;
            },
            getTargetTypes() {
                return AppModel.targetTypes;
            },
            targetTypeName(id) {
                const list = this.getTargetTypes() || [];
                const item = list.find(function(it) { return it && it.id === id; });
                return item ? item.name : '';
            },
            targetTypeIdOf(entry, index) {
                if(!entry) return 'i' + index;
                if(entry.target_type_id != null) return entry.target_type_id;
                if(entry.target_type && entry.target_type.id != null) return entry.target_type.id;
                return 'i' + index;
            },
            targetTypeLabel(entry) {
                if(entry && entry.target_type && entry.target_type.name) {
                    return entry.target_type.name;
                }
                const id = entry
                    ? (entry.target_type_id != null
                        ? entry.target_type_id
                        : (entry.target_type && entry.target_type.id))
                    : null;
                const name = this.targetTypeName(id);
                return name || '';
            },
            getCancellationTermName(id) {
                let result = '';
                let arrCt = this.getCancellationTerms();
                arrCt.forEach(item => {
                    if(item.id === id) {
                        result = item.pms_key + ' : ' + item.name;
                    }
                });
                return result;
            },
            getSeasons() {
                if(AppModel.onlyDayPrices) {
                    return [{'id': -1, 'is_lock': false, 'name': 'Standardpreis für die Neuanlage'}];
                }
                return AppModel.activeCampground.seasons;
            },
            collapseBox() {
                this.isCollapsed = !this.isCollapsed;
            },
            toggleAttributesModal() {
                this.showAttributesModal = !this.showAttributesModal;
            },
            toggleSuitableModal() {
                this.showSuitableModal = !this.showSuitableModal;
            },
            toggleTargetTypesModal() {
                this.showTargetTypesModal = !this.showTargetTypesModal;
            },
            saveTargetTypes(list) {
                // Ensure unique by target_type_id and normalize to backend shape
                const map = {};
                list.forEach(function(item) {
                    if(item && item.target_type_id != null) {
                        map[item.target_type_id] = item;
                    }
                });

                const cleaned = Object.keys(map).map(id => {
                    const it = map[id];
                    return {
                        target_type: { id: Number(id) },
                        distance_min: it.distance_min == null ? null : Number(it.distance_min),
                        distance_max: it.distance_max == null ? null : Number(it.distance_max),
                        walk_time_min: it.walk_time_min == null ? null : Number(it.walk_time_min),
                        walk_time_max: it.walk_time_max == null ? null : Number(it.walk_time_max),
                        accessible: !!it.accessible,
                        notes: it.notes || ''
                    };
                }).filter(function(row) {
                    // keep only rows that have at least some meaningful data
                    const hasDistance = row.distance_min != null || row.distance_max != null;
                    const hasWalk = row.walk_time_min != null || row.walk_time_max != null;
                    const hasAccessible = !!row.accessible;
                    const hasNotes = row.notes && row.notes.trim() !== '';
                    return hasDistance || hasWalk || hasAccessible || hasNotes;
                });

                this.campsiteCopy.target_types = cleaned;
                this.toggleTargetTypesModal();
            },
            async saveAttributes(attributes) {
                const updatedAttributes = (await Api.attributes.setAccomodations(this.value.id, attributes)).data;
                this.toggleAttributesModal();
                this.$emit('update-attributes', updatedAttributes[0]);
            },
            async saveSuitables(suitables) {
                const updatedSuitables = (await Api.suitables.setSuitablesForCampsite(this.value.id, suitables)).data;
                this.toggleSuitableModal();
                this.$emit('update-suitables', updatedSuitables[0]);
            },
            // Liefert das TopX-Attribut-Objekt (mit name und icon_svg) anhand einer ID
            topXById(id) {
                if(id == null) return null;
                const list = this.getTopXAttributes && this.getTopXAttributes();
                if(!list) return null;
                // Falls bereits ein Array
                if(Array.isArray(list)) {
                    return list.find(it => String(it.id) === String(id)) || null;
                }
                // Falls es kein Array ist (z.B. Map/Objekt), versuche Werte zu durchsuchen
                try {
                    const values = Object.values(list);
                    return values.find(it => String(it.id) === String(id)) || null;
                } catch (e) {
                    return null;
                }
            },
            // Erzeugt das Payload-Objekt für das Backend und reduziert die TopX-Attribute auf ihre IDs bzw. null
            buildCampsitePayload() {
                const payload = Object.assign({}, this.campsiteCopy);
                const toIdOrNull = (val) => {
                    if(val == null) return null;
                    if(typeof val === 'object') {
                        return val.id != null ? val.id : null;
                    }
                    // falls bereits eine ID (number/string) geliefert wird
                    return val;
                };

                payload.top1_attribute = toIdOrNull(this.campsiteCopy.top1_attribute);
                payload.top2_attribute = toIdOrNull(this.campsiteCopy.top2_attribute);
                payload.top3_attribute = toIdOrNull(this.campsiteCopy.top3_attribute);

                return payload;
            },
            saveCampsite() {
                this.$refs.observer.validate().then(result => {
                    if(this.campsiteCopy.id) {
                        this.updateCampsite();
                    } else {
                        this.addCampsite();
                    }
                }).catch(result => {
                    console.log('From invalid!', result);
                    Tracking.trackEvent('Campsite', 'updateCampsite', 'formInvalid', null);
                });
            },

            addCampsite() {
                this.onUpdateStart();
                this.campsiteCopy.campground_slug = AppModel.activeCampground.slug;
                const payload = this.buildCampsitePayload();
                Api.campsites.addCampsite(payload)
                    .then((response) => {
                        if(response.ok) {
                            this.campsiteCopy.id = response.body.id;
                            let arrivalWeekdays = {
                                0: false,
                                1: false,
                                2: false,
                                3: false,
                                4: false,
                                5: false,
                                6: false,
                            };
                            let departureWeekdays = {
                                0: false,
                                1: false,
                                2: false,
                                3: false,
                                4: false,
                                5: false,
                                6: false,
                            };
                            Object.values(response.data.arrival_weekdays).forEach(day => {
                                arrivalWeekdays[Number(day)] = true;
                            });
                            Object.values(response.data.departure_weekdays).forEach(day => {
                                departureWeekdays[Number(day)] = true;
                            });
                            response.data.arrival_weekdays = arrivalWeekdays;
                            response.data.departure_weekdays = departureWeekdays;
                            this.onUpdateDone(response.data);
                        } else {
                            this.onUpdateFailed(response);
                        }
                    }, () => {
                        this.onUpdateFailed();
                    });
                Tracking.trackEvent('Campsite', 'saveCampsite', null, null);
            },

            updateCampsite() {
                this.onUpdateStart();
                this.campsiteCopy.campground_slug = AppModel.activeCampground.slug;
                this.oldQuantity = this.value.quantity;
                const payload = this.buildCampsitePayload();
                Api.campsites.updateCampsite(payload)
                    .then((response) => {
                        if(response.ok) {
                            this.onUpdateDone(response.data);
                        } else {
                            this.onUpdateFailed(response);
                        }
                    }, () => {
                        this.onUpdateFailed();
                    });
                Tracking.trackEvent('Campsite', 'updateCampsite', null, null);
            },

            deleteCampsite() {
                this.$emit('del');
            },

            toggleEditMode() {
                this.isEditMode = !this.isEditMode;
                // if editor leaves edit mode remove unsaved season
                // or restore initial data
                if(!this.isEditMode) {
                    if(!this.campsiteCopy.id) {
                        this.deleteCampsite();
                    } else {
                        this.campsiteCopy = Object.assign({}, cloneDeep(this.value));
                        this.buildModel();
                    }
                }
                Tracking.trackEvent('Campsite', 'toggleEditMode', null, null);
            },

            async copyCampsite() {
                await Api.campsites.copyCampsite(this.campsiteCopy.id);
                this.$emit('copy-campsite');
            },

            getSeasonPrice(id) {
                let so = find(this.value.prices, {'season_id': id});
                if(so && so.value) return so.value;
                else return undefined;
            },

            onPriceChanged(id, value) {
                let so = find(this.campsiteCopy.prices, {'season_id': id});
                if(so) {
                    so.value = value;
                } else {
                    this.campsiteCopy.prices.push({'season_id': id, 'value': value});
                }
            },

            onUpdateStart() {
                this.isLoading = true;
            },

            onUpdateFailed(response = null) {
                if(response) {
                    console.log('ERROR:', response.body);
                    if(typeof response.body !== 'string') {
                        let parent = this;
                        Object.entries(response.body).forEach(function(element) {
                            const field = element[0];
                            const messages = element[1];
                            if(parent.$refs.hasOwnProperty(field)) {
                                parent.$refs[field].applyResult({
                                    errors: messages,
                                    valid: false,
                                    failedRules: {}
                                });
                            } else {
                                if(!parent.apiErrors.hasOwnProperty(field)) {
                                    parent.apiErrors[field] = messages;
                                } else {
                                    parent.apiErrors[field].push(...messages);
                                }
                            }
                        });
                    } else {
                        EventBus.$emit(EventBus.SHOW_NOTIFICATION, { title: `<h3>${this.$t('errors.500.title')}</h3>`, direction: 'Up', type: 'danger', body: this.$t('errors.500.content') });
                    }
                } else {
                    EventBus.$emit(EventBus.SHOW_NOTIFICATION, { title: `<h3>${this.$t('generic.formErrorHeadline')}</h3>`, direction: 'Up', type: 'danger', body: this.$t('generic.formError') });
                }
                this.isLoading = false;
                Tracking.trackEvent('Campsite', 'updateCampsite', 'failed', null);
            },

            onUpdateDone(data) {
                this.isLoading = false;
                this.isEditMode = false;
                this.$emit('input', data);
                EventBus.$emit(EventBus.UPDATE_CHECKS);
                EventBus.$emit(EventBus.SHOW_NOTIFICATION, { title: `<h3>${this.$t('generic.saveSuccessHeadline')}</h3>`, direction: 'Up', body: this.$t('generic.saveSuccess') });
                Tracking.trackEvent('Campsite', 'updateCampsite', 'success', null);

                if(data.warnings && data.warnings['quantity-too-low']) {
                    this.minQuantity = Number(data.warnings['quantity-too-low']);
                    this.showQuantityTooLowWarning = true;
                }
            },

            buildModel() {
                this.prices = {};
                for(let i in this.seasons) {
                    this.$set(this.prices, this.seasons[i].id, this.getSeasonPrice(this.seasons[i].id));
                }
            },

            fromFieldClicked(fieldName) {
                Tracking.trackEvent('Campsite', 'formField', fieldName, null);
            },

            setSort(to) {
                this.$emit('sort', to);
            },

            hideQuantityTooLowWarning(keep) {
                this.showQuantityTooLowWarning = false;
                if(!keep) {
                    this.campsiteCopy.quantity = this.minQuantity;
                    this.saveCampsite();
                }
            },
        },
    };
</script>

<style lang="scss">
@import "campsite.scss";
</style>

<style lang="scss" scoped>
.attribute-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.attribute-item {
    display: flex;
    align-items: center;
    gap: 0.15rem;
}

.collapse-handle {
    padding-left: 1rem;
    flex-grow: 1;
    cursor: pointer;
}

.input-big {
    width: 600px !important;
}

.edit-box {
    /*max-height: 1000px;*/
    max-height: 100%;
    overflow: hidden;
    transition: all 0.35s ease;

    &.is-collapsed {
        max-height: 0;
        padding: 1rem;
    }

    &__dragger {
        display: flex;
        align-items: center;
    }
}
</style>

<style scoped>
.attribute-list.two-column {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem 1rem;
    list-style: none;
    padding: 0;
}

.attribute-item {
    display: flex;
    align-items: flex-start;

    i {
        margin-right: 0.5rem;
        flex-shrink: 0;
    }
}

/* Responsive: 1 Spalte auf Mobile */
@media (max-width: 768px) {
    .attribute-list.two-column {
        grid-template-columns: 1fr;
    }
}
</style>



// WEBPACK FOOTER //
// src/views/components/campsite/campsite.vue